/* =========================================================
   BOX IMPORT — Minijuego "Reparto Express"
   Archivo: assets/box-import-game.js
   Aparece cuando el cliente se queda sin conexión o cuando una página tarda en cargar.
   Dibujos: assets/bi-juego-sprites.webp (camión, autos, ítems) y assets/bi-juego-veredas.jpg,
   hechos con Higgsfield. Se descargan cuando la página ya cargó (así no frenan la tienda)
   y quedan en memoria: si después se corta internet, el juego los sigue usando.
   Si todavía no llegaron, se dibuja una versión simple con formas.
   ========================================================= */
(() => {
  if (customElements.get('bi-delivery-game')) return;

  const BEST_KEY = 'bi-game-best';
  const W = 360;
  const H = 520;
  const SIDE = 40;                      // ancho de cada vereda
  const LANES = 3;
  const LANE_W = (W - SIDE * 2) / LANES;
  const NAVY = '#1F2B40';
  const MUSTARD = '#F5CF52';
  const MAX_VIDAS = 3;

  // Recortes dentro de bi-juego-sprites.webp: [x, y, ancho, alto]
  const ATLAS = {
    camion: [0, 0, 96, 200], 'auto-rojo': [98, 0, 113, 176], 'auto-azul': [213, 0, 116, 176],
    'auto-verde': [331, 0, 124, 176], 'auto-taxi': [457, 0, 122, 176], paquete: [581, 0, 95, 96],
    rayo: [678, 0, 80, 96], corazon: [760, 0, 108, 96], cono: [870, 0, 91, 96],
  };
  const AUTOS = ['auto-rojo', 'auto-azul', 'auto-verde', 'auto-taxi'];
  const COLORES = { 'auto-rojo': '#E4572E', 'auto-azul': '#3FA7D6', 'auto-verde': '#59CD90', 'auto-taxi': '#222' };

  const KM_NIVEL = 3;                   // cada cuántos km se sube de nivel

  const laneX = (lane) => SIDE + lane * LANE_W + LANE_W / 2;
  const rand = (min, max) => min + Math.random() * (max - min);

  // Sonidos cortos generados (sin archivos). Arrancan recién cuando el cliente toca "Jugar".
  const sonido = {
    ctx: null,
    on: true,
    init() {
      try { this.on = localStorage.getItem('bi-game-sound') !== '0'; } catch (e) { /* sin storage */ }
      if (this.ctx) return;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) this.ctx = new AC();
    },
    toggle() {
      this.on = !this.on;
      try { localStorage.setItem('bi-game-sound', this.on ? '1' : '0'); } catch (e) { /* sin storage */ }
      return this.on;
    },
    tono(freq, dur = 0.08, tipo = 'square', vol = 0.05, slide = 0) {
      if (!this.on || !this.ctx) return;
      const t = this.ctx.currentTime;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = tipo;
      o.frequency.setValueAtTime(freq, t);
      if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(40, freq + slide), t + dur);
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g).connect(this.ctx.destination);
      o.start(t);
      o.stop(t + dur);
    },
    paquete() { this.tono(880, 0.07); setTimeout(() => this.tono(1320, 0.08), 60); },
    dorado() { [988, 1319, 1568, 1976].forEach((f, i) => setTimeout(() => this.tono(f, 0.09, 'triangle', 0.06), i * 55)); },
    turbo() { this.tono(300, 0.35, 'sawtooth', 0.04, 900); },
    vida() { this.tono(660, 0.1, 'triangle', 0.06); setTimeout(() => this.tono(990, 0.14, 'triangle', 0.06), 90); },
    choque() { this.tono(160, 0.3, 'sawtooth', 0.07, -120); },
    porPoco() { this.tono(1200, 0.05, 'triangle', 0.04); },
    nivel() { [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => this.tono(f, 0.12, 'triangle', 0.05), i * 90)); },
  };

  // Dibujos: se cargan después de que la página terminó de cargar
  const arte = { sprites: null, veredas: null };
  const cargarArte = () => {
    const src = window.BiGameArt;
    if (!src || arte.sprites) return;
    for (const k of ['sprites', 'veredas']) {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => { arte[k] = img; };
      img.src = src[k];
    }
  };
  if (document.readyState === 'complete') setTimeout(cargarArte, 1500);
  else window.addEventListener('load', () => setTimeout(cargarArte, 1500), { once: true });

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  /* ---------- Motor del juego ---------- */
  class DeliveryRun {
    constructor(canvas, { onScore, onGameOver, bestKey = BEST_KEY } = {}) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.onScore = onScore;
      this.onGameOver = onGameOver;
      this.bestKey = bestKey;
      this.best = 0;
      try { this.best = Number(localStorage.getItem(this.bestKey)) || 0; } catch (e) { /* sin storage */ }
      cargarArte();
      this.resize();
      this.reset();
      this.state = 'ready';
      this.draw();
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = W * dpr;
      this.canvas.height = H * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.ctx.imageSmoothingQuality = 'high';
    }

    reset() {
      this.player = { lane: 1, x: laneX(1), y: H - 128, w: 46, h: 96, tilt: 0 };
      this.items = [];
      this.sparks = [];
      this.popups = [];
      this.speed = 240;
      this.score = 0;
      this.road = 0;
      this.side = 0;
      this.spawnIn = 0.6;
      this.time = 0;
      this.shake = 0;
      this.vidas = MAX_VIDAS;
      this.inmune = 0;       // segundos sin daño después de un choque
      this.turbo = 0;        // segundos de turbo (invencible y puntos dobles)
      this.combo = 0;
      this.km = 0;
      this.nivel = 1;
      this.banner = 0;       // segundos que queda visible el cartel de nivel
      this.fila = 0;         // paquetes que quedan por soltar en fila
      this.filaLane = 1;
    }

    start() {
      sonido.init();
      sonido.ctx?.resume?.();
      this.reset();
      this.banner = 1.4;
      this.state = 'playing';
      this.onScore?.(0, this.best);
      this.resume();
    }

    resume() {
      if (this.raf || this.state !== 'playing') return;
      this.last = performance.now();
      const loop = (t) => {
        const dt = Math.min((t - this.last) / 1000, 0.05);
        this.last = t;
        this.update(dt);
        this.draw();
        this.raf = this.state === 'playing' || this.shake > 0 ? requestAnimationFrame(loop) : 0;
      };
      this.raf = requestAnimationFrame(loop);
    }

    pause() {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    move(dir) {
      if (this.state !== 'playing') return;
      const antes = this.player.lane;
      this.player.lane = Math.max(0, Math.min(LANES - 1, this.player.lane + dir));
      if (this.player.lane !== antes) this.player.tilt = dir * 0.22;
    }

    // Carriles con un obstáculo cerca de la parte de arriba (incluye el carril al que dobla un auto)
    carrilesOcupados() {
      const s = new Set();
      for (const i of this.items) {
        if ((i.type !== 'car' && i.type !== 'cono') || i.y > 200) continue;
        s.add(i.lane);
        if (i.toLane !== undefined) s.add(i.toLane);
      }
      return s;
    }

    spawn() {
      // Fila de paquetes: se sueltan de a uno en el mismo carril
      if (this.fila > 0) {
        this.fila--;
        this.items.push({ type: 'pkg', lane: this.filaLane, x: laneX(this.filaLane), spin: rand(0, Math.PI), y: -60, w: 40, h: 40 });
        return;
      }
      // Nunca bloqueamos los tres carriles: siempre queda un camino libre
      const blocked = this.carrilesOcupados();
      const lane = Math.floor(Math.random() * LANES);
      const r = Math.random();
      let type = r < 0.08 ? 'fila' : r < 0.3 ? 'pkg' : r < 0.32 ? 'dorado' : r < 0.36 ? 'rayo' : r < 0.385 && this.vidas < MAX_VIDAS ? 'corazon' : r < 0.5 ? 'cono' : 'car';
      if ((type === 'car' || type === 'cono') && blocked.size >= LANES - 1 && !blocked.has(lane)) type = 'pkg';
      if (type === 'fila') {
        this.fila = 3 + Math.floor(Math.random() * 3);
        this.filaLane = lane;
        return this.spawn();
      }
      const base = { type, lane, x: laneX(lane), spin: rand(0, Math.PI) };
      if (type === 'car') {
        const sprite = AUTOS[Math.floor(Math.random() * AUTOS.length)];
        // Cada auto va a su velocidad: todos avanzan para el mismo lado que el camión, más despacio
        const auto = { ...base, sprite, y: -110, w: 50, h: 78, vel: rand(0.6, 0.82) };
        // Desde el nivel 2, algunos autos cambian de carril (ponen el guiño antes)
        if (this.nivel >= 2 && Math.random() < Math.min(0.45, 0.15 + this.nivel * 0.06)) {
          const opciones = [lane - 1, lane + 1].filter((l) => l >= 0 && l < LANES && !blocked.has(l));
          if (opciones.length && blocked.size < LANES - 2) {
            auto.toLane = opciones[Math.floor(Math.random() * opciones.length)];
            auto.dobla = rand(60, 170);   // altura de pantalla donde empieza a doblar
          }
        }
        this.items.push(auto);
      } else if (type === 'dorado') {
        this.items.push({ ...base, y: -60, w: 44, h: 44 });
      } else if (type === 'cono') {
        this.items.push({ ...base, y: -50, w: 32, h: 34 });
      } else {
        this.items.push({ ...base, y: -60, w: 40, h: 40 });
      }
    }

    update(dt) {
      if (this.shake > 0) this.shake = Math.max(0, this.shake - dt);
      if (this.state !== 'playing') return;

      this.time += dt;
      this.speed = Math.min(640, 240 + this.time * 8 + (this.nivel - 1) * 25) * (this.turbo > 0 ? 1.25 : 1);
      this.road = (this.road + this.speed * dt) % 60;
      this.side += this.speed * dt;
      this.km += this.speed * dt / 4000;
      this.inmune = Math.max(0, this.inmune - dt);
      this.turbo = Math.max(0, this.turbo - dt);
      this.banner = Math.max(0, this.banner - dt);
      // Subida de nivel
      const nivel = 1 + Math.floor(this.km / KM_NIVEL);
      if (nivel > this.nivel) {
        this.nivel = nivel;
        this.banner = 1.8;
        this.score += 5;
        this.onScore?.(this.score, this.best);
        this.popups.push({ x: W / 2, y: H / 2 + 40, life: 1.2, text: '+5 bonus' });
        sonido.nivel();
      }

      // El camión se desliza suave hacia su carril y se endereza
      const p = this.player;
      p.x += (laneX(p.lane) - p.x) * Math.min(1, dt * 14);
      p.tilt *= Math.max(0, 1 - dt * 8);

      this.spawnIn -= dt;
      if (this.spawnIn <= 0) {
        this.spawn();
        // Las filas de paquetes salen pegaditas; el resto, con aire entre medio
        this.spawnIn = this.fila > 0 ? 50 / this.speed : rand(0.45, 0.85) * (240 / this.speed) + 0.18;
      }

      const hitbox = { x: p.x - p.w / 2 + 7, y: p.y + 8, w: p.w - 14, h: p.h - 14 };
      for (const it of this.items) {
        it.y += this.speed * dt * (it.type === 'car' ? it.vel : 1);
        it.spin += dt * 3;
        // Auto que cambia de carril: guiño primero, después se desliza al carril nuevo
        if (it.toLane !== undefined && it.y > it.dobla) {
          it.x += (laneX(it.toLane) - it.x) * Math.min(1, dt * 3.2);
          it.giro = (it.toLane - it.lane) * 0.12 * Math.max(0, 1 - Math.abs(laneX(it.toLane) - it.x) / LANE_W);
          if (Math.abs(laneX(it.toLane) - it.x) < 2) { it.x = laneX(it.toLane); it.lane = it.toLane; it.toLane = undefined; it.giro = 0; }
        }
        const box = { x: it.x - it.w / 2 + 3, y: it.y + 3, w: it.w - 6, h: it.h - 6 };
        const hit = box.x < hitbox.x + hitbox.w && box.x + box.w > hitbox.x && box.y < hitbox.y + hitbox.h && box.y + box.h > hitbox.y;
        // "¡Por poco!": un auto pasa pegado al camión sin tocarlo
        if (!hit && !it.dead && !it.rozado && it.type === 'car' && it.y > p.y + p.h - 10 && Math.abs(it.x - p.x) < LANE_W * 1.05) {
          it.rozado = true;
          this.score += 1;
          this.onScore?.(this.score, this.best);
          this.popups.push({ x: (it.x + p.x) / 2, y: p.y + 10, life: 0.8, text: '¡Por poco! +1' });
          sonido.porPoco();
        }
        if (!hit || it.dead) continue;
        if (it.type === 'pkg' || it.type === 'dorado') {
          it.dead = true;
          this.combo++;
          const base = it.type === 'dorado' ? 5 : 1;
          const puntos = base * (this.turbo > 0 ? 2 : 1) * Math.min(5, 1 + Math.floor(this.combo / 5));
          this.score += puntos;
          this.onScore?.(this.score, this.best);
          this.popups.push({ x: it.x, y: it.y, life: 0.8, text: it.type === 'dorado' ? `¡Dorado! +${puntos}` : `+${puntos}` });
          this.burst(it.x, it.y + 10, MUSTARD, it.type === 'dorado' ? 24 : 10);
          if (it.type === 'dorado') sonido.dorado(); else sonido.paquete();
        } else if (it.type === 'rayo') {
          it.dead = true;
          this.turbo = 4;
          this.popups.push({ x: it.x, y: it.y, life: 1, text: '¡Turbo!' });
          this.burst(it.x, it.y + 10, '#FFE27A');
          sonido.turbo();
        } else if (it.type === 'corazon') {
          it.dead = true;
          this.vidas = Math.min(MAX_VIDAS, this.vidas + 1);
          this.popups.push({ x: it.x, y: it.y, life: 1, text: '+1 vida' });
          this.burst(it.x, it.y + 10, '#FF6B6B');
          sonido.vida();
        } else if (this.turbo > 0) {
          // Con turbo, los obstáculos salen volando
          it.dead = true;
          this.burst(it.x, it.y + it.h / 2, '#FFFFFF');
          this.shake = 0.12;
        } else if (this.inmune <= 0) {
          it.dead = true;
          this.golpe();
          if (this.state !== 'playing') return;
        }
      }
      this.items = this.items.filter((i) => !i.dead && i.y < H + 120);
      this.sparks = this.sparks.filter((s) => (s.life -= dt) > 0);
      this.sparks.forEach((s) => { s.x += s.vx * dt; s.y += s.vy * dt; s.vy += 400 * dt; });
      this.popups = this.popups.filter((s) => (s.life -= dt) > 0);
    }

    burst(x, y, color, n = 10) {
      for (let i = 0; i < n; i++) this.sparks.push({ x, y, vx: rand(-140, 140), vy: rand(-180, -40), life: 0.55, color });
    }

    golpe() {
      sonido.choque();
      this.vidas--;
      this.combo = 0;
      this.shake = 0.3;
      this.inmune = 1.6;
      if (this.vidas <= 0) this.crash();
    }

    crash() {
      this.state = 'over';
      this.shake = 0.35;
      if (this.score > this.best) {
        this.best = this.score;
        try { localStorage.setItem(this.bestKey, String(this.best)); } catch (e) { /* sin storage */ }
      }
      this.onGameOver?.(this.score, this.best, Math.round(this.km * 10) / 10, this.nivel);
    }

    /* ---------- Dibujo ---------- */
    sprite(nombre, cx, cy, w, h) {
      const c = this.ctx;
      const a = ATLAS[nombre];
      if (!arte.sprites || !a) return false;
      c.drawImage(arte.sprites, a[0], a[1], a[2], a[3], cx - w / 2, cy - h / 2, w, h);
      return true;
    }

    draw() {
      const c = this.ctx;
      c.save();
      if (this.shake > 0) c.translate(rand(-6, 6) * this.shake * 3, rand(-6, 6) * this.shake * 3);

      this.drawRoad();
      for (const it of this.items) this.drawItem(it);
      this.drawTruck(this.player);

      // Chispas y textos
      for (const s of this.sparks) {
        c.globalAlpha = Math.max(0, s.life * 2);
        c.fillStyle = s.color || MUSTARD;
        c.beginPath();
        c.arc(s.x, s.y, 3.2, 0, Math.PI * 2);
        c.fill();
      }
      c.globalAlpha = 1;
      c.font = '800 20px Manrope, system-ui, sans-serif';
      c.textAlign = 'center';
      for (const s of this.popups) {
        c.globalAlpha = Math.min(1, s.life * 2);
        c.lineWidth = 4;
        c.strokeStyle = NAVY;
        c.strokeText(s.text, s.x, s.y - (0.8 - s.life) * 50);
        c.fillStyle = MUSTARD;
        c.fillText(s.text, s.x, s.y - (0.8 - s.life) * 50);
      }
      c.globalAlpha = 1;

      this.drawHud();
      this.drawBanner();
      c.restore();
    }

    drawBanner() {
      if (this.banner <= 0 || this.state !== 'playing') return;
      const c = this.ctx;
      const a = Math.min(1, this.banner * 2);
      const esc = 1 + Math.max(0, this.banner - 1.4) * 1.2;
      c.save();
      c.globalAlpha = a;
      c.translate(W / 2, H / 2 - 30);
      c.scale(esc, esc);
      c.fillStyle = 'rgba(31, 43, 64, .88)';
      roundRect(c, -110, -34, 220, 68, 18); c.fill();
      c.strokeStyle = MUSTARD; c.lineWidth = 3;
      roundRect(c, -110, -34, 220, 68, 18); c.stroke();
      c.fillStyle = MUSTARD;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.font = '900 30px Manrope, system-ui, sans-serif';
      c.fillText(`Nivel ${this.nivel}`, 0, -4);
      c.font = '700 12px Manrope, system-ui, sans-serif';
      c.fillStyle = '#fff';
      c.fillText(this.nivel === 1 ? '¡Arrancamos el reparto!' : this.nivel === 2 ? 'Ojo: ahora los autos cambian de carril' : 'Más tráfico, más velocidad', 0, 20);
      c.textBaseline = 'alphabetic';
      c.restore();
    }

    drawRoad() {
      const c = this.ctx;
      // Asfalto con un leve degradé
      const g = c.createLinearGradient(SIDE, 0, W - SIDE, 0);
      g.addColorStop(0, '#26344D'); g.addColorStop(0.5, '#2E3E5C'); g.addColorStop(1, '#26344D');
      c.fillStyle = g;
      c.fillRect(-10, -10, W + 20, H + 20);
      // Veredas con casas y árboles (imagen repetible)
      if (arte.veredas) {
        const v = arte.veredas;
        const half = v.width / 2;
        const th = v.height * (SIDE / half);
        const off = this.side % th;
        for (let y = off - th; y < H; y += th) {
          c.drawImage(v, 0, 0, half, v.height, 0, y, SIDE, th);
          c.drawImage(v, half, 0, half, v.height, W - SIDE, y, SIDE, th);
        }
      } else {
        c.fillStyle = '#E9DFC8';
        c.fillRect(0, 0, SIDE, H);
        c.fillRect(W - SIDE, 0, SIDE, H);
      }
      // Bordes amarillos y líneas de carril
      c.fillStyle = MUSTARD;
      c.fillRect(SIDE, 0, 4, H);
      c.fillRect(W - SIDE - 4, 0, 4, H);
      c.fillStyle = 'rgba(255, 255, 255, .55)';
      for (let l = 1; l < LANES; l++) {
        for (let y = -60 + this.road; y < H; y += 60) c.fillRect(SIDE + l * LANE_W - 2, y, 4, 30);
      }
      // Estela de velocidad con turbo
      if (this.turbo > 0) {
        c.fillStyle = 'rgba(245, 207, 82, .18)';
        for (let i = 0; i < 6; i++) c.fillRect(SIDE + 12 + ((i * 47 + this.side * 0.6) % (W - SIDE * 2 - 24)), (i * 97 + this.side * 1.4) % H, 2, 40);
      }
    }

    drawHud() {
      const c = this.ctx;
      this.drawPill(10, 10, `📦 ${this.score}`, 'left');
      this.drawPill(W - 10, 10, `Récord ${Math.max(this.best, this.score)}`, 'right');
      // Vidas
      for (let i = 0; i < MAX_VIDAS; i++) {
        c.globalAlpha = i < this.vidas ? 1 : 0.25;
        if (!this.sprite('corazon', 24 + i * 24, 58, 22, 20)) {
          c.fillStyle = '#FF5A5A';
          c.beginPath(); c.arc(24 + i * 24, 58, 8, 0, Math.PI * 2); c.fill();
        }
      }
      c.globalAlpha = 1;
      // Nivel y km
      this.drawPill(10, 76, `Nivel ${this.nivel} · ${this.km.toFixed(1).replace('.', ',')} km`, 'left', 'rgba(255, 255, 255, .8)');
      // Combo y turbo
      const combo = Math.min(5, 1 + Math.floor(this.combo / 5));
      if (combo > 1) this.drawPill(W - 10, 50, `Combo x${combo}`, 'right', MUSTARD);
      if (this.turbo > 0) {
        c.fillStyle = 'rgba(255, 255, 255, .25)';
        roundRect(c, W / 2 - 50, H - 18, 100, 6, 3); c.fill();
        c.fillStyle = MUSTARD;
        roundRect(c, W / 2 - 50, H - 18, 100 * (this.turbo / 4), 6, 3); c.fill();
      }
    }

    drawPill(x, y, text, align, fondo = 'rgba(255, 255, 255, .95)') {
      const c = this.ctx;
      c.font = '800 14px Manrope, system-ui, sans-serif';
      const w = c.measureText(text).width + 22;
      const left = align === 'left' ? x : x - w;
      c.fillStyle = 'rgba(0, 0, 0, .18)';
      roundRect(c, left, y + 2, w, 30, 15); c.fill();
      c.fillStyle = fondo;
      roundRect(c, left, y, w, 30, 15); c.fill();
      c.fillStyle = NAVY;
      c.textAlign = 'left';
      c.textBaseline = 'middle';
      c.fillText(text, left + 11, y + 16);
      c.textBaseline = 'alphabetic';
    }

    drawTruck(p) {
      const c = this.ctx;
      const parpadea = this.inmune > 0 && Math.floor(this.inmune * 10) % 2 === 0;
      c.save();
      c.translate(p.x, p.y + p.h / 2);
      c.rotate(p.tilt);
      if (this.turbo > 0) {
        const g = c.createRadialGradient(0, 0, 10, 0, 0, 70);
        g.addColorStop(0, 'rgba(245, 207, 82, .55)'); g.addColorStop(1, 'rgba(245, 207, 82, 0)');
        c.fillStyle = g;
        c.beginPath(); c.arc(0, 0, 70, 0, Math.PI * 2); c.fill();
      }
      c.globalAlpha = parpadea ? 0.35 : 1;
      c.fillStyle = 'rgba(0, 0, 0, .28)';
      roundRect(c, -p.w / 2 + 4, -p.h / 2 + 8, p.w, p.h, 10); c.fill();
      if (!this.sprite('camion', 0, 0, p.w, p.h)) {
        c.fillStyle = NAVY; roundRect(c, -p.w / 2 + 4, -p.h / 2, p.w - 8, 28, 9); c.fill();
        c.fillStyle = '#FFF8E4'; roundRect(c, -p.w / 2, -p.h / 2 + 24, p.w, p.h - 24, 8); c.fill();
        c.fillStyle = MUSTARD; c.fillRect(-5, -p.h / 2 + 26, 10, p.h - 28);
      }
      c.restore();
      c.globalAlpha = 1;
    }

    drawItem(it) {
      const c = this.ctx;
      const cy = it.y + it.h / 2;
      if (it.type === 'car') {
        c.fillStyle = 'rgba(0, 0, 0, .28)';
        roundRect(c, it.x - it.w / 2 + 3, it.y + 6, it.w, it.h, 12); c.fill();
        // Los dibujos de los autos miran hacia abajo: los giramos 180° para que avancen
        // de frente, para el mismo lado que el camión (si no, parece que van en reversa)
        c.save();
        c.translate(it.x, cy);
        c.rotate(Math.PI + (it.giro || 0));
        if (!this.sprite(it.sprite, 0, 0, it.w, it.h)) {
          c.fillStyle = COLORES[it.sprite]; roundRect(c, -it.w / 2, -it.h / 2, it.w, it.h, 12); c.fill();
        }
        c.restore();
        // Guiño: parpadea del lado al que va a doblar
        if (it.toLane !== undefined && Math.floor(this.time * 6) % 2 === 0) {
          const lado = it.toLane > it.lane ? 1 : -1;
          c.fillStyle = '#FFB020';
          c.shadowColor = '#FFB020'; c.shadowBlur = 10;
          for (const dy of [4, it.h - 10]) { c.beginPath(); c.arc(it.x + lado * (it.w / 2 - 3), it.y + dy, 4, 0, Math.PI * 2); c.fill(); }
          c.shadowBlur = 0;
        }
        return;
      }
      if (it.type === 'cono') {
        if (!this.sprite('cono', it.x, cy, it.w, it.h)) {
          c.fillStyle = '#FF8C42'; c.beginPath(); c.moveTo(it.x, it.y); c.lineTo(it.x + 14, it.y + it.h); c.lineTo(it.x - 14, it.y + it.h); c.fill();
        }
        return;
      }
      // Ítems que se juntan: flotan y brillan
      const bob = Math.sin(it.spin * 2) * 3;
      const dorado = it.type === 'dorado';
      const brillo = it.type === 'rayo' ? 'rgba(255, 226, 122, .4)' : it.type === 'corazon' ? 'rgba(255, 107, 107, .3)' : dorado ? 'rgba(255, 215, 64, .55)' : 'rgba(245, 207, 82, .3)';
      c.fillStyle = brillo;
      c.beginPath(); c.arc(it.x, cy + bob, (dorado ? 32 : 27) + Math.sin(it.spin * 3) * (dorado ? 5 : 2), 0, Math.PI * 2); c.fill();
      if (dorado) {
        // Rayos de luz girando alrededor del paquete dorado
        c.save();
        c.translate(it.x, cy + bob);
        c.rotate(it.spin);
        c.fillStyle = 'rgba(255, 236, 140, .55)';
        for (let k = 0; k < 6; k++) { c.rotate(Math.PI / 3); c.fillRect(-2, -38, 4, 12); }
        c.restore();
      }
      c.save();
      c.translate(it.x, cy + bob);
      c.rotate(Math.sin(it.spin) * 0.18);
      const nombre = it.type === 'pkg' || dorado ? 'paquete' : it.type;
      if (dorado) c.filter = 'sepia(1) saturate(4) hue-rotate(-8deg) brightness(1.15)';
      if (!this.sprite(nombre, 0, 0, it.w, it.h)) {
        c.fillStyle = it.type === 'pkg' ? '#C8915A' : it.type === 'rayo' || dorado ? MUSTARD : '#FF5A5A';
        roundRect(c, -17, -17, 34, 34, 6); c.fill();
      }
      c.filter = 'none';
      c.restore();
    }
  }

  // Lo exponemos para reutilizarlo en la página "Tu envío"
  window.BiDeliveryRun = DeliveryRun;

  /* ---------- Pantalla (sin conexión / carga lenta) ---------- */
  class BiDeliveryGame extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };
      this.delay = (Number(this.dataset.delay) || 1.5) * 1000;
      this.titleEl = this.querySelector('[data-game-title]');
      this.subEl = this.querySelector('[data-game-sub]');
      this.layerStart = this.querySelector('[data-game-start]');
      this.layerOver = this.querySelector('[data-game-over]');
      this.onlineEl = this.querySelector('[data-game-online]');
      this.screen = this.querySelector('.bi-game__screen');

      this.game = new DeliveryRun(this.querySelector('canvas'), {
        onGameOver: (score, best, km, nivel) => {
          const n = this.querySelector('[data-final-nivel]');
          if (n) n.textContent = nivel || 1;
          this.querySelector('[data-final-score]').textContent = score;
          this.querySelector('[data-final-best]').textContent = best;
          const k = this.querySelector('[data-final-km]');
          if (k) k.textContent = String(km || 0).replace('.', ',');
          const t = this.querySelector('[data-final-titulo]');
          if (t) t.textContent = score >= best && score > 0 ? '¡Nuevo récord!' : score >= 30 ? '¡Repartidor estrella!' : score >= 10 ? '¡Buen reparto!' : '¡Choque!';
          this.layerOver.hidden = false;
        },
      });

      const play = () => {
        this.layerStart.hidden = true;
        this.layerOver.hidden = true;
        this.game.start();
      };
      this.querySelectorAll('[data-game-play]').forEach((b) => b.addEventListener('click', play, opts));
      this.querySelector('[data-game-left]')?.addEventListener('pointerdown', (e) => { e.preventDefault(); this.game.move(-1); }, opts);
      this.querySelector('[data-game-right]')?.addEventListener('pointerdown', (e) => { e.preventDefault(); this.game.move(1); }, opts);
      this.querySelector('[data-game-close]')?.addEventListener('click', () => this.close(), opts);
      const btnSonido = this.querySelector('[data-game-sound]');
      const pintarSonido = () => { if (btnSonido) { btnSonido.textContent = sonido.on ? '🔊' : '🔇'; btnSonido.setAttribute('aria-pressed', String(!sonido.on)); } };
      try { sonido.on = localStorage.getItem('bi-game-sound') !== '0'; } catch (e) { /* sin storage */ }
      pintarSonido();
      btnSonido?.addEventListener('click', () => { sonido.toggle(); pintarSonido(); }, opts);
      this.querySelector('[data-game-back]')?.addEventListener('click', () => {
        if (this.pendingUrl) window.location.href = this.pendingUrl;
        else this.close();
      }, opts);

      // Toque / deslizamiento sobre la ruta
      let startX = null;
      this.screen.addEventListener('pointerdown', (e) => {
        if (e.target.closest('button')) return;
        startX = e.clientX;
      }, opts);
      this.screen.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        const r = this.screen.getBoundingClientRect();
        if (Math.abs(dx) > 30) this.game.move(dx > 0 ? 1 : -1);
        else this.game.move(e.clientX - r.left < r.width / 2 ? -1 : 1);
        startX = null;
      }, opts);

      document.addEventListener('keydown', (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') { this.game.move(-1); e.preventDefault(); }
        if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') { this.game.move(1); e.preventDefault(); }
        if ((e.key === ' ' || e.key === 'Enter') && this.game.state !== 'playing') { play(); e.preventDefault(); }
        if (e.key === 'Escape') this.close();
        if (e.key === 'm' || e.key === 'M') { sonido.toggle(); pintarSonido(); }
      }, opts);

      // Sin conexión
      window.addEventListener('offline', () => this.open('offline'), opts);
      window.addEventListener('online', () => {
        if (this.mode === 'offline' && this.classList.contains('is-open')) this.onlineEl.hidden = false;
      }, opts);

      // Cargas lentas: si la página nueva tarda, mostramos el juego
      document.addEventListener('click', (e) => this.onLinkClick(e), opts);
      document.addEventListener('submit', (e) => this.onSubmit(e), opts);
      window.addEventListener('pageshow', () => { clearTimeout(this.loadTimer); if (this.mode === 'loading') this.close(); }, opts);
      window.addEventListener('pagehide', () => clearTimeout(this.loadTimer), opts);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.game.pause();
        else if (this.classList.contains('is-open')) this.game.resume();
      }, opts);

      // Editor de Shopify: se muestra al seleccionar la sección
      if (this.dataset.designMode === 'true') {
        document.addEventListener('shopify:section:select', (e) => {
          if (this.closest('.shopify-section')?.id === `shopify-section-${e.detail.sectionId}`) this.open('loading');
        }, opts);
        document.addEventListener('shopify:section:deselect', (e) => {
          if (this.closest('.shopify-section')?.id === `shopify-section-${e.detail.sectionId}`) this.close();
        }, opts);
      }
    }

    disconnectedCallback() {
      this.ac?.abort();
      clearTimeout(this.loadTimer);
      this.game?.pause();
    }

    isNavigation(a) {
      if (!a || a.hasAttribute('download')) return false;
      if (a.target && a.target !== '_self') return false;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return false;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return false; // anclas en la misma página
      return url;
    }

    onLinkClick(e) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const url = this.isNavigation(e.target.closest?.('a[href]'));
      if (!url) return;
      if (!navigator.onLine) {
        e.preventDefault();
        this.pendingUrl = url.href;
        this.open('offline');
        return;
      }
      // Esperamos un instante: si otro script maneja el clic (carrito, menú), no hacemos nada
      setTimeout(() => { if (!e.defaultPrevented) this.armLoading(); }, 0);
    }

    onSubmit(e) {
      if (!navigator.onLine) {
        e.preventDefault();
        this.open('offline');
        return;
      }
      setTimeout(() => { if (!e.defaultPrevented) this.armLoading(); }, 0);
    }

    armLoading() {
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => this.open('loading'), this.delay);
    }

    open(mode) {
      this.mode = mode;
      const offline = mode === 'offline';
      this.titleEl.textContent = offline ? this.dataset.offlineTitle : this.dataset.loadingTitle;
      this.titleEl.classList.toggle('bi-game__dots', !offline);
      this.subEl.textContent = offline ? this.dataset.offlineText : this.dataset.loadingText;
      this.onlineEl.hidden = true;
      if (this.game.state === 'over') {
        this.layerOver.hidden = true;
        this.layerStart.hidden = false;
        this.game.state = 'ready';
        this.game.reset();
        this.game.draw();
      }
      this.game.resize();
      this.game.draw();
      this.classList.add('is-open');
      document.documentElement.classList.add('bi-game-lock');
      this.game.resume();
      this.querySelector('[data-game-play]')?.focus({ preventScroll: true });
    }

    close() {
      clearTimeout(this.loadTimer);
      this.classList.remove('is-open');
      document.documentElement.classList.remove('bi-game-lock');
      this.game.pause();
    }
  }

  customElements.define('bi-delivery-game', BiDeliveryGame);
})();
